import { useEffect, useRef } from "react";

export const useScrollHandler = (onScrollUp: () => void, onScrollDown: () => void) => {
  const lastScrollYByTarget = useRef(new WeakMap<EventTarget, number>());

  useEffect(() => {
    const handleScroll = (event: Event) => {
      const target = event.target ?? window;

      // The Wall's modals/overlays (e.g. the generosity wallet) lock the body with
      // `position: fixed` and, on close, restore the page position via `window.scrollTo`.
      // That programmatic jump fires a `scroll` event that must not be read as a user
      // scroll — otherwise the QBS collapses to its header whenever such an overlay closes.
      // While the body is locked we drop the stored baseline so the restore jump only
      // re-establishes it instead of registering as a scroll-down.
      if (typeof document !== "undefined" && document.body.style.position === "fixed") {
        lastScrollYByTarget.current.delete(target);
        return;
      }

      const currentScrollY =
        target === document || target === window
          ? window?.scrollY ?? 0
          : "scrollTop" in target
          ? Number(target.scrollTop)
          : 0;
      const lastScrollY = lastScrollYByTarget.current.get(target);

      if (lastScrollY !== undefined) {
        const scrollDelta = currentScrollY - lastScrollY;

        if (scrollDelta > 0) {
          onScrollDown();
        } else if (scrollDelta < 0) {
          onScrollUp();
        }
      }

      lastScrollYByTarget.current.set(target, currentScrollY);
    };

    window?.addEventListener("scroll", handleScroll, { passive: true, capture: true });

    return () => {
      window?.removeEventListener("scroll", handleScroll, { capture: true });
    };
  }, [onScrollUp, onScrollDown]);
};
